import { type CreditCode, type DiscountCode } from './computeCartTotalDetails'
import { PublicListingV1, PublicListingSession } from '@kouto/types'
import {
  computeCartTotalDetails,
  type CartTotalDetails,
  type NonIncludedTax,
  type IncludedTax,
  type Listing as CartListing,
  CartItemTotalDetails,
} from './computeCartTotalDetails'
import { hasCompoundTaxFeatures, type Tax } from './compoundTaxHelpers'
import { computeCartItemWithCompoundTaxes } from './computeCartItemWithCompoundTaxes'

export enum BookingMode {
  PRIVATE = 'private',
  SHARED = 'shared',
}

export enum CartItemState {
  PENDING = 'PENDING',
  READY = 'READY',
}

export interface CustomQuestionResponse {
  key: string | null
  value: boolean | string
}

export interface ICartParticipant {
  customQuestionResponses?: {
    [questionId: string]: CustomQuestionResponse[]
  }
  emailAddress?: string
  firstName: string
  fullName?: string
  id?: string
  lastName: string
  phoneNumber?: string
  price: number
  priceTier?: string
  priceTierName: string
  terms: boolean
}

export interface CartItemAddOn {
  id: string
  label: string
  price: number
  selectedNumber: number
  taxes: PublicListingV1['taxes']
}

export interface ICartExperience {
  additionalCustomQuestionResponses: {
    [questionId: string]: CustomQuestionResponse[]
  }
  addOns?: CartItemAddOn[]
  bookingMode: BookingMode
  cartItemId: string
  endDate?: string
  experienceId: string
  groupId?: string
  groupTitle?: string
  listingId?: string
  maxParticipantCountForPrivateBooking?: number
  notes?: string
  participants: ICartParticipant[]
  sessionDateTime: string
  sessionDuration: string
  sessions?: PublicListingSession[]
  startDate?: string
  state: CartItemState
  validationMessages?: string[]
  waitlistEntryId?: string
}

const deduplicateIncludedTaxes = (taxes: IncludedTax[]): IncludedTax[] => {
  const seen = new Set<string>()
  return taxes.filter(tax => {
    const key = `${tax.name}-${tax.percentage}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const aggregateNonIncludedTaxes = (
  existingMap: Map<string, NonIncludedTax>,
  newTaxes: NonIncludedTax[],
): Map<string, NonIncludedTax> => {
  const result = new Map(existingMap)

  newTaxes.forEach(tax => {
    const existing = result.get(tax.name)

    if (existing) {
      result.set(tax.name, {
        ...existing,
        amountFee: existing.amountFee + tax.amountFee,
      })
    } else {
      result.set(tax.name, { ...tax })
    }
  })
  return result
}

const hasAnyCompoundTaxes = (
  cartItems: Record<string, ICartExperience>,
  listings: Record<string, CartListing>,
): boolean => {
  return Object.values(cartItems).some(cartItem => {
    if (!cartItem.listingId) return false
    const listing = listings[cartItem.listingId]
    if (!listing) return false

    // Get the correct taxes based on groupId (for resource experiences) or listing level
    const listingTaxes = (() => {
      if (cartItem.groupId && listing.resourceGroups) {
        const resourceGroup = listing.resourceGroups.find(
          group => group.id === cartItem.groupId,
        )
        return resourceGroup?.taxes || []
      }
      return listing.taxes || []
    })() as Tax[]
    return hasCompoundTaxFeatures(listingTaxes)
  })
}

const calculateCartItemsWithCompoundTaxes = (
  cartItems: Record<string, ICartExperience>,
  listings: Record<string, CartListing>,
  baseCalculation: CartTotalDetails,
): {
  cartItemsDetails: Record<string, CartItemTotalDetails>
  cartSubTotal: number
  cartTotal: number
  includedTaxes: IncludedTax[]
  nonIncludedTaxes: NonIncludedTax[]
  totalDiscount: number
} => {
  const result = Object.entries(cartItems).reduce(
    (acc, [cartItemId, cartItem]) => {
      if (!cartItem.listingId) return acc

      const listing = listings[cartItem.listingId]
      if (!listing) return acc

      const baseItemDetails = baseCalculation.cartItemsDetails[cartItemId]
      const itemCalculation = computeCartItemWithCompoundTaxes(
        cartItem,
        listing,
        baseItemDetails?.percentageDiscountByCode || 0,
        baseItemDetails?.fixedPercentageDiscount || 0,
        baseItemDetails?.fixedAmountDiscount || 0,
        false,
        baseItemDetails?.fixedExperienceAmountDiscount || 0,
      )

      return {
        cartItemsDetails: {
          ...acc.cartItemsDetails,
          [cartItemId]: itemCalculation,
        },
        cartSubTotal: acc.cartSubTotal + itemCalculation.subTotal,
        cartTotal: acc.cartTotal + itemCalculation.total,
        includedTaxes: [...acc.includedTaxes, ...itemCalculation.includedTaxes],
        nonIncludedTaxesMap: aggregateNonIncludedTaxes(
          acc.nonIncludedTaxesMap,
          itemCalculation.nonIncludedTaxes,
        ),
        totalDiscount: acc.totalDiscount + itemCalculation.totalDiscountAmount,
      }
    },
    {
      cartItemsDetails: {} as Record<string, CartItemTotalDetails>,
      cartSubTotal: 0,
      cartTotal: 0,
      includedTaxes: [] as IncludedTax[],
      nonIncludedTaxesMap: new Map<string, NonIncludedTax>(),
      totalDiscount: 0,
    },
  )

  return {
    cartItemsDetails: result.cartItemsDetails,
    cartSubTotal: result.cartSubTotal,
    cartTotal: result.cartTotal,
    includedTaxes: deduplicateIncludedTaxes(result.includedTaxes),
    nonIncludedTaxes: Array.from(result.nonIncludedTaxesMap.values()),
    totalDiscount: result.totalDiscount,
  }
}

const applyCreditCodes = (
  cartTotal: number,
  creditCodes: CreditCode[],
): { finalTotal: number; totalCreditDiscount: number } => {
  const totalCreditDiscount = creditCodes.reduce(
    (sum, credit) => sum + credit.value,
    0,
  )

  return {
    finalTotal: Math.max(0, cartTotal - totalCreditDiscount),
    totalCreditDiscount,
  }
}

export const computeCartTotalWithCompoundTaxes = (
  cartItems: Record<string, ICartExperience>,
  listings: Record<string, CartListing>,
  options?: {
    creditCodes?: null | CreditCode[]
    discountCode?: null | DiscountCode['discountCode']
    fixedAmountDiscount?: number
    fixedPercentageDiscount?: number
  },
): CartTotalDetails => {
  const baseCalculation = computeCartTotalDetails(cartItems, listings, {
    creditCodes: options?.creditCodes,
    discountCode: options?.discountCode,
    fixedAmountDiscount: options?.fixedAmountDiscount,
    fixedPercentageDiscount: options?.fixedPercentageDiscount,
  })

  if (!hasAnyCompoundTaxes(cartItems, listings)) {
    return baseCalculation
  }

  const compoundCalculation = calculateCartItemsWithCompoundTaxes(
    cartItems,
    listings,
    baseCalculation,
  )

  const { finalTotal, totalCreditDiscount } = applyCreditCodes(
    compoundCalculation.cartTotal,
    options?.creditCodes || [],
  )

  return {
    cartItemsDetails: compoundCalculation.cartItemsDetails,
    cartSubTotal: compoundCalculation.cartSubTotal,
    cartTotal: finalTotal,
    creditCodesTotalDiscountAmount: totalCreditDiscount,
    includedTaxes: compoundCalculation.includedTaxes,
    nonIncludedTaxes: compoundCalculation.nonIncludedTaxes,
    totalDiscountAmount:
      compoundCalculation.totalDiscount + totalCreditDiscount,
  }
}
